import type { Route } from '../App';
import { Icon, type IconName } from '../components/Icon';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';
import { useMe } from '../data/store';
import { api } from '../data/api';

interface Props {
  route: Route;
  onNavigate: (r: Route) => void;
  open: boolean;
  onClose: () => void;
}

type NavItem = { id: Route; icon: IconName; label: string };

// Tre gruppi con etichetta invece di una lista piatta di 12 voci: il "giorno
// per giorno" (Lavoro), la gestione dello studio (Studio), il resto (Sistema).
// Ogni voce ha la SUA icona — niente più icone doppie (Pipeline/Documenti).
const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Lavoro',
    items: [
      { id: 'overview', icon: 'home', label: 'Panoramica' },
      { id: 'ops', icon: 'alert', label: 'Operatività' },
      { id: 'clients', icon: 'building', label: 'Clienti' },
      { id: 'leads', icon: 'message', label: 'Lead' },
      { id: 'templates', icon: 'template', label: 'Modelli' },
      { id: 'pipeline', icon: 'funnel', label: 'Pipeline' },
    ],
  },
  {
    label: 'Studio',
    items: [
      { id: 'revenue', icon: 'chart', label: 'Ricavi' },
      { id: 'expenses', icon: 'tag', label: 'Spese' },
      { id: 'partners', icon: 'users', label: 'Partner' },
      { id: 'documenti', icon: 'folder', label: 'Documenti' },
      { id: 'vault', icon: 'lock', label: 'Cassaforte' },
    ],
  },
  {
    label: 'Sistema',
    items: [{ id: 'settings', icon: 'gear', label: 'Impostazioni' }],
  },
];

// Studio-level sections a partner doesn't get. (Documenti SÌ: i partner ne
// vedono una versione filtrata — il Patto coi soci resta solo all'operatore.)
const PARTNER_HIDDEN: Route[] = ['partners', 'expenses', 'settings'];

function initialsOf(s: string): string {
  return s
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function Sidebar({ route, onNavigate, open, onClose }: Props) {
  const me = useMe();
  const isPartner = me?.role === 'partner';

  const name = me?.name ?? me?.email ?? '—';
  const avatar = initialsOf(me?.name ?? me?.email ?? 'R');
  const roleLabel = me
    ? `${me.role === 'operator' ? 'Operatore' : 'Partner'} · ${me.workspace.name.replace(' (house)', '')}`
    : '';

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      window.location.reload();
    }
  };

  return (
    <>
      {open && <div className="scrim-side" onClick={onClose} aria-hidden="true" />}
      <aside className={'side' + (open ? ' open' : '')}>
        <div className="side-brand">
          <span className="rs-logo" aria-hidden="true">R</span>
          <span className="rs-brand-meta">
            <span className="rs-brand-name">
              Risma <em>studio</em>
            </span>
            <span className="rs-brand-sub">Console</span>
          </span>
        </div>

        <WorkspaceSwitcher />

        <nav className="side-nav" aria-label="Primary">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((it) => !(isPartner && PARTNER_HIDDEN.includes(it.id)));
            if (items.length === 0) return null;
            return (
              <div className="nav-group" key={group.label}>
                <div className="nav-group-label">{group.label}</div>
                {items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    className={'nav-item' + (route === it.id ? ' active' : '')}
                    aria-current={route === it.id ? 'page' : undefined}
                    onClick={() => onNavigate(it.id)}
                  >
                    <Icon name={it.icon} size={17} className="ico" />
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="side-foot">
          <div className="rs-user">
            <span className="rs-user-av" aria-hidden="true">{avatar}</span>
            <span className="rs-user-meta">
              <span className="rs-user-name">{name}</span>
              <span className="rs-user-role">{roleLabel}</span>
            </span>
          </div>
          <button type="button" className="rs-logout" onClick={logout}>Esci</button>
        </div>
      </aside>
    </>
  );
}
